import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { MenuItem, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { APP_ICONS } from "../../../assets";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import NavigationDrawer from "../../../components/NavigationDrawer";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import CustomSelect from "../../../components/customSelect";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getAllAppointments } from "../../../services/appointmentApiFn";
import { getAllCompanyForFilters } from "../../../services/comanyApiFn";
import { getAllWorkerForFilters } from "../../../services/workerApiFn";
import { handleOpenSnackBar } from "../../../utils";
import AppLoader from "./../../../components/AppLoader/index";

const headerData = [
  "createdAt",
  "appointmentId",
  "service",
  "worker",
  "company",
  "appointmentStatus",
  "workerStatus",
  "cost",
  "actions",
];

const tableRows = [
  "created_date",
  "appointment_gen_id",
  "appointment_service",
  "appointment_worker",
  "appointment_company",
  "appointment_status",
  "appointment_worker_status",
  "appointment_cost",
  "detail_chip",
];

const WORKER_STATUS = [
  {
    value: "Pending",
    type: "pending",
  },
  {
    value: "Accepted",
    type: "accepted",
  },
  {
    value: "Rejected",
    type: "rejected",
  },
];

const APPOINTMENT_STATUS = [
  {
    value: "Pending",
    type: "pending",
  },
  {
    value: "Arrived",
    type: "accepted",
  },
  {
    value: "Started",
    type: "started",
  },
  {
    value: "Completed",
    type: "completed",
  },
];

function AppointmentPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["appointment", "common"]);
  const [isLoading, setIsLoading] = useState();
  const [allAppointments, setAppointments] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]);
  const userData = useSelector((state) => state.userData.user);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  useEffect(() => {
    if (userData) {
      fetchAllAppointments(
        searchKeys.appointmentByWorkerStatus,
        searchKeys.appointmentByStatus,
        searchKeys.appointmentByCompany,
        searchKeys.appointmentByWorker
      );
    }
  }, [
    userData,
    searchKeys.appointmentByWorkerStatus,
    searchKeys.appointmentByStatus,
    searchKeys.appointmentByCompany,
    searchKeys.appointmentByWorker,
  ]);

  useEffect(() => {
    fetchFiltersData();
  }, []);

  const fetchAllAppointments = async (
    workerStatus,
    status,
    company,
    worker
  ) => {
    const query = {
      workerStatus,
      status,
      company,
      worker,
    };
    try {
      setIsLoading(true);
      const response = await getAllAppointments(query);
      if (response?.data.status === "success") {
        let data = response?.data?.data;
        setAppointments(data);
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFiltersData = async () => {
    try {
      const [companiesResponse, workersResponse] = await Promise.all([
        getAllCompanyForFilters(),
        getAllWorkerForFilters(),
      ]);

      // Handle companies response
      if (companiesResponse?.data.status === "success") {
        setAllCompanies(companiesResponse?.data?.data);
      } else {
        handleOpenSnackBar("error", companiesResponse?.data.message);
      }

      // Handle workers response
      if (workersResponse?.data.status === "success") {
        setAllWorkers(workersResponse?.data?.data);
      } else {
        handleOpenSnackBar("error", workersResponse?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    }
  };

  const handleViewDetail = (row) => {
    navigate(`/appointments/details/${row?._id}`);
  };

  const renderHeaderAction = () => {
    return (
      <Stack flexGrow={1} direction={{ md: "row" }} justifyContent={"flex-end"}>
        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
        />
      </Stack>
    );
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"} gap={1}>
        <CustomSelect
          placeholder={t("allCompanies")}
          formStyleProps={{
            width: { xs: "100%", md: "20%" },
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<ContentCopyIcon sx={{ fontSize: "15px", color: "#000000" }} />}
          value={searchKeys.appointmentByCompany}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                appointmentByCompany: e.target.value,
              })
            );
          }}
        >
          <MenuItem value={"All"}>{t("all")}</MenuItem>
          {allCompanies.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val._id}
                sx={{ textTransform: "capitalize" }}
              >
                {val.name}
              </MenuItem>
            );
          })}
        </CustomSelect>
        <CustomSelect
          placeholder={t("allWorkers")}
          formStyleProps={{
            width: { xs: "100%", md: "20%" },
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<ContentCopyIcon sx={{ fontSize: "15px", color: "#000000" }} />}
          value={searchKeys.appointmentByWorker}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                appointmentByWorker: e.target.value,
              })
            );
          }}
        >
          <MenuItem value={"All"}>{t("all")}</MenuItem>
          {allWorkers.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val._id}
                sx={{ textTransform: "capitalize" }}
              >
                {val.name}
              </MenuItem>
            );
          })}
        </CustomSelect>
        <CustomSelect
          placeholder={t("workerStatus")}
          formStyleProps={{
            width: { xs: "100%", md: "20%" },
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<ContentCopyIcon sx={{ fontSize: "15px", color: "#000000" }} />}
          value={searchKeys.appointmentByWorkerStatus}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                appointmentByWorkerStatus: e.target.value,
              })
            );
          }}
        >
          <MenuItem value={"All"}>{t("all")}</MenuItem>
          {WORKER_STATUS.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val.type}
                sx={{ textTransform: "capitalize" }}
              >
                {val.value}
              </MenuItem>
            );
          })}
        </CustomSelect>
        <CustomSelect
          placeholder={"Status"}
          formStyleProps={{
            width: { xs: "100%", md: "20%" },
            "& .MuiInputBase-input": {
              textTransform: "capitalize",
            },
          }}
          icon={<ContentCopyIcon sx={{ fontSize: "15px", color: "#000000" }} />}
          value={searchKeys.appointmentByStatus}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                appointmentByStatus: e.target.value,
              })
            );
          }}
        >
          <MenuItem value={"All"}>All</MenuItem>
          {APPOINTMENT_STATUS.map((val, ind) => {
            return (
              <MenuItem
                key={ind}
                value={val.type}
                sx={{ textTransform: "capitalize" }}
              >
                {val.value}
              </MenuItem>
            );
          })}
        </CustomSelect>
        <GlobalButton
          btnText={"reset"}
          styleProps={{ width: "auto", height: 40, borderRadius: 1 }}
          handleClickBtn={() => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                appointmentByWorkerStatus: "All",
                appointmentByStatus: "All",
                appointmentByCompany: "All",
                appointmentByWorker: "All",
              })
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("appointmentList")}
        subtitle={t("viewAllAppointmentDetails")}
        // headerActions={renderHeaderAction()}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          minWidth={1500}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allAppointments}
          handleViewDetail={handleViewDetail}
          showPagination={true}
        />
        {allAppointments?.length === 0 && isLoading === false && (
          <Typography
            variant="h5"
            fontWeight={600}
            textAlign={"center"}
            textTransform={"capitalize"}
          >
            {t("noDataFound")}!
          </Typography>
        )}
      </PaperContainer>
    </NavigationDrawer>
  );
}

export default AppointmentPage;
